

// REPO PATH:  src/front-end/code/slices/settings/actions.js
//
// saveSettings now writes to TWO destinations:
//   - tenant-scoped settings (mqtt creds, auth keys, agg defaults, ...)
//       -> PUT /user/update/settings            (unchanged route)
//   - user-scoped preferences (contact info, consent flags, firstName)
//       -> PUT /user/update/preferences         (backend resolves user_sub
//          from the authenticated request; settings service validates,
//          persists to the preferences collection, and rebroadcasts the
//          tenant's consent-gated contact roster to the rules engine)
//
// The read side is merged in the app back-end (interfaces.getConfig), so
// the store keeps one flat settings object and no component changes.

import hooks from '../../actions/hooks';

import { setSettings, setSettingsStatus, updateSettingsData } from './';
import { setTempSettings, setCredential, setUsernameCheckStatus, clearFlags } from './tempSettings';

// Same require style the two slices use (defaultSettings.js is module.exports).
const defaultSettings = require('./defaultSettings');


// The per-user fields. Must stay in sync with what the settings service's
// contacts module reads from preferences.json_data (see settings repo:
// src/database/contacts/index.js) plus defaultAlertEmail, which joins the
// roster in the email step.
const PREF_FIELDS = [
	'firstName',
	'defaultAlertMobileNumber', 'smsEnabled', 'smsConsentAccepted', 'smsConsentNumber',
	'defaultAlertWhatsappNumber', 'whatsappEnabled', 'whatsappConsentAccepted', 'whatsappConsentNumber',
	'defaultAlertEmail',
];

// Split one flat settings form object into { tenant, prefs } payloads.
// Every PREF_FIELD present in data goes to prefs and is EXCLUDED from the
// tenant payload — the legacy tenant copies stay frozen as a read fallback
// for accounts that predate the split, but are never written again.
function splitSettingsPayload(data) {
	const prefs = {};
	const tenant = { ...data };
	PREF_FIELDS.forEach(function(field) {
		if (Object.prototype.hasOwnProperty.call(tenant, field)) {
			prefs[field] = tenant[field];
			delete tenant[field];
		}
	});
	return { tenant, prefs };
}


export const saveSettings = (token) => async (dispatch, getState) => {
	const { settings, tempSettings } = getState();
	dispatch(setSettingsStatus('saving_changes'));
	try {
		const { tenant, prefs } = splitSettingsPayload(tempSettings.data);

		// Both writes together; if either fails we restore rather than
		// leave the two stores half-synced with the form.
		await Promise.all([
			hooks.put('/update/settings', tenant, token),
			hooks.put('/update/preferences', prefs, token),
		]);

		// Sync the saved copy so persisted state (e.g. SMS consent) reflects
		// what was just saved. Keeps Cancel/restore accurate and lets the UI
		// rely on saved values rather than transient edit status.
		// Re-read AFTER the awaits. `tempSettings` above was captured before the
		// network round-trip; a 'settings/updated' broadcast can land while the
		// PUTs are in flight and update both stores. Writing the stale snapshot
		// here put the saved copy out of sync with temp (saved: 'Larry',
		// temp: '') and isDirty then rendered the Save bar on a page that had
		// just been saved successfully.
		dispatch(updateSettingsData(getState().tempSettings.data));
		dispatch(setSettingsStatus('changes_saved'));
		setTimeout(function() {
			console.log("setting dormant")
			dispatch(setSettingsStatus('dormant'));
			dispatch(clearFlags());
		}, 800);

	} catch (error) {
		dispatch(restoreSettings());
		console.error("\txx error saving settings:", error);
	}

	if (tempSettings.data.mqttUsername != "" &&
		tempSettings.data.mqttUsername != settings.data.mqttUsername || 
		tempSettings.data.mqttPassword != settings.data.mqttPassword) {

		// we need to restart the mqtt broker
		// and echo it to the screen.

		console.log("mqtt changed");

		const {mqttUsername, mqttPassword} = tempSettings.data;
		const outgoingUser = settings.data.mqttUsername || "";

		try {
			const response = await hooks.post(
				'/mqtt/put_user', 
				{ username: mqttUsername, password: mqttPassword, outgoingUser }, 
				token
			);

			dispatch(setSettings({
				...settings.data,
				mqttUsername,
				mqttPassword
			}));

			dispatch(setUsernameCheckStatus({ mqttRestart: true }));

			/*	
				previously, i was attempting to confirm it was good.  but in hooks.js, 
				i turned off the inclusion of status in the resopnse
			*/

		} catch (err) {
			console.error(err);
		}
	}
	
};



export const generateCredential = (type, token) => async (dispatch) => {
	try {
		const { credential } = await hooks.get('/credentials/generate', token);
		dispatch(setCredential({ type, value: credential }));
	} catch (error) {
		console.error("\txx error generating credential:", error);
	}
};



export const restoreSettings = () => (dispatch, getState) => {
	const currentSettings = getState().settings.data;
	dispatch(setTempSettings(currentSettings));
	dispatch(clearFlags());
};



// The incoming payload is PARTIAL BY CONSTRUCTION, not just when empty.
// interfaces/getConfig builds config.settings as { ...tenantSettings, ...prefs },
// and the two halves are written separately (see splitSettingsPayload above).
// A fresh account whose prefs doc exists but whose tenant settings blob does not
// yields a NON-EMPTY object holding only PREF_FIELDS -- firstName, alert email,
// consent flags -- with no subscriptionStatus, which is tenant-scoped.
//
// setSettings/setTempSettings replace state.data WHOLESALE (deliberately -- see
// tempSettings.js), so handing that payload straight through discards every key
// it omits, including the subscriptionStatus:'unsubscribed' both stores booted
// with. Undefined then matches neither branch of the Subscription block (blank,
// no Sign Up button) and is not == "unsubscribed", so the credential buttons
// render enabled and fail silently.
//
// Layering the payload OVER defaultSettings keeps the shape complete: real saved
// values still win, only genuinely-absent keys fall back. Both stores get the
// SAME merged object, so isDirty stays false and no Save bar appears on boot.
// The socket 'settings/updated' broadcast carries the TENANT settings doc and
// nothing else. Two facts from the settings service make this exact:
//   - database/config: updateSingleRecord returns the whole merged tenant
//     json_data, and routes/config PUT hands that straight to collectionUpdate.
//   - routes/preferences: collectionUpdate('preferences', ...) is a documented
//     NO-OP ('preferences' is absent from senders/topicMap), so PREF_FIELDS
//     never travel over the socket at all.
//
// Therefore a tenant broadcast may only speak about TENANT fields. Any
// PREF_FIELD sitting in that doc is a frozen legacy copy (see splitSettingsPayload)
// which getConfig deliberately overlays with the preferences collection at boot;
// letting it through here would resurrect a stale value.
//
// Two bugs this closes:
//   - Routing the partial doc through setBothSettings REPLACED the store, so
//     every pref was dropped to its default: saving the salutation blanked it.
//   - Merging onto settings.data alone was order-dependent -- that copy has not
//     picked up the just-typed firstName until saveSettings' post-await write,
//     so a broadcast arriving first still wiped it.
//
// Merge the tenant-only delta onto EACH store's own current data. Unsaved edits
// to pref fields survive; if the user has genuine unsaved changes the two stores
// legitimately differ and isDirty stays true, which is correct.
export const mergeBothSettings = (partial) => (dispatch, getState) => {

	const delta = { ...(partial || {}) };
	PREF_FIELDS.forEach(function(field) { delete delta[field]; });

	const { settings, tempSettings } = getState();
	dispatch(setSettings({ ...settings.data, ...delta }));
	dispatch(setTempSettings({ ...tempSettings.data, ...delta }));
};


export const setBothSettings = (settings) => (dispatch) => {

	const complete = { ...defaultSettings, ...(settings || {}) };

	// // TEMP DEBUG — remove once the fresh-boot settings issue is closed.
	// console.log('[setBothSettings] incoming subscriptionStatus:', JSON.stringify(settings && settings.subscriptionStatus));
	// console.log('[setBothSettings] merged   subscriptionStatus:', JSON.stringify(complete.subscriptionStatus));
	// console.log('[setBothSettings] incoming key count:', settings ? Object.keys(settings).length : 0,
	// 	'-> merged key count:', Object.keys(complete).length);
	// if (settings && Object.prototype.hasOwnProperty.call(settings, 'subscriptionStatus')
	// 	&& settings.subscriptionStatus === undefined) {
	// 	console.warn('[setBothSettings] server sent subscriptionStatus explicitly as undefined — merge will NOT backfill it');
	// }

	dispatch(setSettings(complete));
	dispatch(setTempSettings(complete));
};